import classNames from "classnames";
import { FC } from "react";
import Checkbox from "~/components/Checkbox";
import Icon from "~/components/Icon";
import Tag from "~/components/Tag";
import Text from "~/components/Text";
import Colors from "~/constants/colors";
import { UserAddress } from "~/services/user";
import styles from "./AddressItem.module.scss";
import SkeletonByTimeout from "~/components/Skeletons/SkeletonByTimeout";
import Skeleton from "react-loading-skeleton";
import { isEmpty } from "lodash";

interface AddressItemProps {
  data: UserAddress;
  order: number;
  variant?: "checkbox" | "default";
  onSetAsDefault?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  showTagDefault?: boolean;
  checked?: boolean;
  onChecked?: (checked: boolean) => void;
  setDefaultLoading?: boolean;
  removeLoading?: boolean;
  isCheckout?: boolean;
}

const AddressInfo = ({ text, data }: { text: string; data: UserAddress }) => {
  return (
    <Text type="body-3" color={Colors.GREY_700}>
      {!isEmpty(data) ? (
        <>{text}</>
      ) : (
        <SkeletonByTimeout>
          <Skeleton height={20} containerClassName="w-1/2" />
        </SkeletonByTimeout>
      )}
    </Text>
  );
};

const AddressItem: FC<AddressItemProps> = ({
  data,
  order,
  variant,
  onSetAsDefault,
  onEdit,
  onRemove,
  showTagDefault = true,
  checked,
  onChecked,
  setDefaultLoading,
  removeLoading,
  isCheckout,
}) => {
  const loading = setDefaultLoading || removeLoading;
  return (
    <div
      className={classNames(styles.address_item, "cursor-pointer", {
        [styles.active]: checked,
      })}
      onClick={() => onChecked?.(!checked)}
    >
      <>
        {variant === "checkbox" && (
          <Checkbox
            checked={checked}
            onChange={(e) => onChecked?.(e.target.checked)}
          />
        )}
        <div className={styles.content}>
          <div className="flex items-center gap-2">
            {!isEmpty(data) ? (
              <>
                <Text type="label-4">Address {order}</Text>
                {data?.default_shipping && showTagDefault && (
                  <Tag
                    name="Default"
                    backgroundColor={Colors.ACCENT_RED_100}
                    nameType="body-3"
                    nameColor={Colors.PRIMARY}
                  />
                )}
              </>
            ) : (
              <SkeletonByTimeout>
                <Skeleton height={20} containerClassName="w-1/2" />
              </SkeletonByTimeout>
            )}
          </div>
          <AddressInfo
            text={`${data?.firstname} ${data?.lastname}`}
            data={data}
          />
          <AddressInfo text={data?.telephone} data={data} />
          <AddressInfo
            text={`${data?.street?.[0]}, ${data?.city}, ${data?.country_code}`}
            data={data}
          />
          <AddressInfo text={data.postcode} data={data} />
        </div>
        {!isCheckout && (
          <div className={styles.action} onClick={(e) => e.stopPropagation()}>
            <Icon
              name="more-3"
              color={Colors.GREY_100}
              className="cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />

            <div
              className={styles.popover}
              style={loading ? { opacity: 1 } : {}}
            >
              {onSetAsDefault && (
                <Text
                  className={classNames(styles.action_item, {
                    [styles.active]: checked,
                  })}
                  type="body-3"
                  color={Colors.GREY_800}
                  onClick={() => {
                    !setDefaultLoading && onSetAsDefault?.();
                  }}
                >
                  <div className="flex gap-2 items-center">
                    {setDefaultLoading && (
                      <span className="inline-flex">
                        <Icon
                          className="animate-spin"
                          name="status"
                          size={18}
                        />
                      </span>
                    )}
                    <span>Set as default</span>
                  </div>
                </Text>
              )}
              {onEdit && (
                <Text
                  className={styles.action_item}
                  type="body-3"
                  color={Colors.GREY_800}
                  onClick={onEdit}
                >
                  Edit
                </Text>
              )}
              {onRemove && (
                <Text
                  className={styles.action_item}
                  type="body-3"
                  color={Colors.ACCENT_RED}
                  onClick={() => {
                    !removeLoading && onRemove?.();
                  }}
                >
                  <div className="flex gap-2 items-center">
                    {removeLoading && (
                      <span className="inline-flex">
                        <Icon
                          className="animate-spin"
                          name="status"
                          size={18}
                        />
                      </span>
                    )}
                    <span>Remove</span>
                  </div>
                </Text>
              )}
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default AddressItem;
